import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import dayjs from '../../../../../utils/dateUtils';

import { MEDIA_TYPES, MEDIA_UPLOAD_STATUS } from '../../../../../constants';
import Image from './image/Image';
import Video from './video/Video';
import { ICONS } from '../../../../../icons';
import CommonIconButton from '../../../../../components/common-icon-button/CommonIconButton';
import CommonTooltipEllipses from '../../../../../components/common-tooltip-ellipses/CommonTooltipEllipses';
import MultiselectDropdownNew from '../../../../../components/multiselect-dropdown/MultiselectDropdownNew';
import CommonButton, { BUTTON_STYLES } from '../../../../../components/common-button/CommonButton';
import LoadingSection from './loading-section/LoadingSection';
import { useProject } from '../../../../../context/ProjectContext';
import { addLocalTag } from '../../../../../redux/activ8/activ8Reducer';
import DuplicateSection from './duplicate-section/DuplicateSection';
import File from './file/File';
import WithLabel, { LABEL_STYLES } from '../../../../../components/with-label/WithLabel';
import CommonSwitch from '../../../../../components/common-switch/CommonSwitch';
import CommonDatePicker from '../../../../../components/common-date-picker/CommonDatePicker';

import scss from './MediaCard.module.scss';

function MediaCard({
  item,
  onRemoveItem,
  onUpdateItemTags,
  onUpdateItemThirdPartyRights,
  onUpdateItemExpirationDate,
  onMediaErrorLoading,
  allTagsOptions,
  onCopyTags,
  onPasteTags,
  duplicates,
  onClearDuplicates,
}) {
  const { t } = useTranslation('activ8');
  const { actions } = useProject();
  const dispatch = useDispatch();
  const { id: projectId } = useParams();

  const tagsOptions = useMemo(() => item.tags.map((tag) => ({ value: tag, label: tag })), [item.tags]);
  const isTagsLoading = useSelector((state) => state.activ8.isTagsLoading);

  const handleMediaErrorLoading = (item) => {
    onMediaErrorLoading(item);
  };

  const handleUpdateTags = (tags) => {
    onUpdateItemTags(
      item.id,
      tags.map((tag) => tag.value),
    );
  };

  const handleAddNewTag = (tag) => {
    dispatch(addLocalTag(tag.value));
  };

  const handleSearchTags = (value) => {
    actions('searchTags', { id: projectId, query: value });
  };

  const handleClearDuplicates = () => {
    onClearDuplicates(item.id);
  };

  const handleRemoveItem = () => {
    onRemoveItem(item.id);
  };

  const handleThirdPartyRightsChange = (event) => {
    onUpdateItemThirdPartyRights(item.id, event.target.checked);
  };

  const handleExpirationDateChange = (date) => {
    onUpdateItemExpirationDate(item.id, date);
  };

  return (
    <div className={scss.card}>
      <div className={scss.header}>
        <CommonTooltipEllipses label={item.name} className={scss.name} />

        <CommonIconButton icon={ICONS.Delete} onClick={handleRemoveItem} iconClassName={scss.deleteIcon} />
      </div>
      <div className={scss.mediaWrapper}>
        {item.type === MEDIA_TYPES.IMAGE ? <Image item={item} onError={handleMediaErrorLoading} /> : null}
        {item.type === MEDIA_TYPES.VIDEO ? <Video item={item} onError={handleMediaErrorLoading} /> : null}
        {item.type === MEDIA_TYPES.APPLICATION ? <File item={item} onConvertRequest={handleMediaErrorLoading} /> : null}
      </div>

      {duplicates?.length ? (
        <DuplicateSection
          onClearDuplicates={handleClearDuplicates}
          duplicates={duplicates}
          onRemoveDuplicated={handleRemoveItem}
        />
      ) : null}

      <div className={scss.fieldsWrapper}>
        <WithLabel label={t('expirationDate')} variant={LABEL_STYLES.COLUMN} isTwoDots isBold>
          <CommonDatePicker
            selected={item.expirationDate}
            onChange={handleExpirationDateChange}
            minDate={dayjs().startOf('day').toDate()}
            isClearable
            placeholder={t('expirationDate')}
          />
        </WithLabel>
        <WithLabel label={t('thirdPartyRights')} variant={LABEL_STYLES.COLUMN} isTwoDots isBold>
          <CommonSwitch checked={item.thirdPartyRights ?? false} onChange={handleThirdPartyRightsChange} />
        </WithLabel>
      </div>

      <div className={scss.tagsWrapper}>
        <MultiselectDropdownNew
          value={tagsOptions}
          options={allTagsOptions}
          onChange={handleUpdateTags}
          onSearch={handleSearchTags}
          isInitialSearch={false}
          onAddNewOption={handleAddNewTag}
          isLoading={isTagsLoading}
          isDebounce
          placeholder={t('tags')}
          name='media-card-tags'
          usePortal
        />
      </div>

      <div className={scss.actions}>
        <CommonButton styleColor={BUTTON_STYLES.SECONDARY} onClick={() => onCopyTags(item.id)} className={scss.copy}>
          {t('copyTags')}
        </CommonButton>
        <CommonButton styleColor={BUTTON_STYLES.SECONDARY} onClick={() => onPasteTags(item.id)} className={scss.paste}>
          {t('pasteTags')}
        </CommonButton>
      </div>

      {item.status !== MEDIA_UPLOAD_STATUS.PENDING ? (
        <LoadingSection status={item.status} progress={item.progress} />
      ) : null}
    </div>
  );
}

export default MediaCard;
